import { useEffect, useState, useMemo, useRef } from 'react'
import { Modal, Button, Row, Form } from 'react-bootstrap'
import { AsyncTypeahead } from 'react-bootstrap-typeahead'
import { useDebouncedCallback } from 'use-debounce'
import { Plus } from 'lucide-react'
import moment from 'moment'
import { Formik } from 'formik'
import api from '../../../config/AxiosInstance'
import * as Common from '../../../utils/Common'
import ModalAddBatch from '../batch/ModalAddBatch'

const labelDetailStyle = {
  fontSize: '0.75rem',
  fontWeight: '700',
  color: '#999',
  textTransform: 'uppercase',
  marginBottom: '0.25rem',
  letterSpacing: '0.5px'
}

const StyledSelectInput = ({
  label,
  value,
  name,
  handleChange,
  handleBlur,
  touched,
  errors,
  options,
  required,
  disabled,
  col = 12,
  placeholder,
  hasData,
  reff
}) => {
  return (
    <Form.Group className={`mb-3 col-12 col-md-${col}`}>
      <Form.Label style={labelDetailStyle}>
        {label} {required && <sup className="text-danger">*</sup>}
      </Form.Label>
      <Form.Select
        name={name}
        ref={reff}
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        isInvalid={touched?.[name] && !!errors?.[name]}
        disabled={disabled}
        className={`px-3 ${disabled ? 'bg-light' : 'bg-white'}`}
        style={{
          fontSize: '0.9rem',
          height: '40px',
          borderRadius: '8px',
          border: '1px solid #ced4da'
        }}
      >
        <option value="">{placeholder || 'Select'}</option>
        {options.map((option, idx) => (
          <option
            key={idx}
            value={option.id || option.value}
            hasData={option.hasData || option.has_category}
          >
            {option.name || option.item_name || option.label}
          </option>
        ))}
      </Form.Select>
      <Form.Control.Feedback type="invalid">
        {errors?.[name]}
      </Form.Control.Feedback>
    </Form.Group>
  )
}

const StyledTextField = ({
  label,
  value,
  name,
  handleChange,
  handleBlur,
  touched,
  errors,
  type = 'text',
  placeholder,
  required,
  disabled,
  as,
  col = 12
}) => {
  return (
    <Form.Group className={`mb-3 col-12 col-md-${col}`}>
      <Form.Label style={labelDetailStyle}>
        {label} {required && <sup className="text-danger">*</sup>}
      </Form.Label>
      <Form.Control
        as={as}
        type={type}
        name={name}
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        isInvalid={touched?.[name] && !!errors?.[name]}
        placeholder={placeholder}
        disabled={disabled}
        className={`px-3`}
        style={{
          fontSize: '0.9rem',
          height: as === 'textarea' ? 'auto' : '40px',
          borderRadius: '8px',
          border: '1px solid #ced4da',
          backgroundColor: disabled ? '#e9ecef' : '#fff',
          cursor: disabled ? 'not-allowed' : 'default'
        }}
        autoComplete="off"
      />
      <Form.Control.Feedback type="invalid">
        {errors?.[name]}
      </Form.Control.Feedback>
    </Form.Group>
  )
}

const ModalAddItemPr = ({ show, onHide, prId, items, loadData, detail }) => {
  const [itemId, setItemId] = useState(0)
  const [inputBatch, setInputBatch] = useState('')
  const [batchId, setBatchId] = useState(0)
  const [batchSelectedDetail, setBatchSelectedDetail] = useState(null)
  const [options, setOptions] = useState([])
  const [newOption, setNewOption] = useState(null)
  const [uomOptions, setUomOptions] = useState([])
  const [selectedUom, setSelectedUom] = useState(null)
  const [dataItemSelect, setDataItemSelect] = useState(null)
  const [totalPack, setTotalPack] = useState({
    total_qty: 0,
    conversion_uom: '',
    conversion_qty_uom: '',
    conversion_factor: ''
  })

  const [modalAddBatch, setModalAddBatch] = useState(false)
  const refBatch = useRef()
  const refItems = useRef()

  useEffect(() => {
    if (!show) {
      setItemId(0)
      setInputBatch('')
      setBatchId(0)
      setBatchSelectedDetail(null)
      setOptions([])
      setNewOption(null)
      setUomOptions([])
      setSelectedUom(null)
      setDataItemSelect(null)
      setTotalPack({
        total_qty: 0,
        conversion_uom: '',
        conversion_qty_uom: '',
        conversion_factor: ''
      })
      if (refBatch.current) refBatch.current.clear()
    }
  }, [show])

  const handleSearchBatch = useDebouncedCallback(async (query) => {
    const params = {
      item_id: itemId,
      q: query
    }
    const res = await api.get('inbound/pr-draft/show-batches', { params })
    setOptions(res.data.data)
    setNewOption({ batch: query })
  }, 1000)

  async function handleClickItem(id, setFieldValue) {
    let selectedItem = null
    items.forEach((b) => {
      if (b.id === id) {
        setDataItemSelect(b)
        selectedItem = b
      }
    })
    setItemId(id)
    if (id && id !== '0' && id !== 0) {
      try {
        const res = await api.get(`/master/items/${id}/uoms`)
        const uomData = res.data.data
        setUomOptions(
          uomData.map((u) => ({ id: u.uom_name, name: u.uom_name, ...u }))
        )
        const defaultUom = uomData.find((u) => u.is_pack) || uomData[0]
        setSelectedUom(defaultUom)
        setFieldValue('receiving_uom', defaultUom?.uom_name || '')
        setTotalPack((prevData) => {
          const uomObj =
            typeof selectedItem?.uom === 'object' ? selectedItem?.uom : null
          return {
            ...prevData,
            total_qty: 0,
            conversion_uom: defaultUom?.uom_name || '',
            conversion_qty_uom: uomObj
              ? uomObj.conversion_uom
              : selectedItem?.uom || '',
            conversion_factor: uomObj
              ? uomObj.conversion_factor
              : defaultUom?.convertion_factor || 0
          }
        })
      } catch (err) {
        const errMsg =
          err?.response?.data?.errors?.message || 'An error occurred'
        Common.showPopUpErrorMessage(errMsg)
      }
    }
  }

  const onAddItem = async (values) => {
    try {
      const rawDateMd = inputBatch?.newBatch
        ? inputBatch?.manufacturing_date
        : batchSelectedDetail?.manufacturing_date
      const rawDateEd = inputBatch?.newBatch
        ? inputBatch?.exp_date
        : batchSelectedDetail?.exp_date

      const body = {
        batch_id: batchId,
        total_pack: values.total_pack_add,
        receiving_uom: selectedUom?.uom_name,
        date_md: rawDateMd ? moment(rawDateMd).format('YYYY-MM-DD') : null,
        date_ed: rawDateEd ? moment(rawDateEd).format('YYYY-MM-DD') : null,
        note: ''
      }

      if (!body.date_md || !body.date_ed) {
        Common.showPopUpErrorMessage('Batch MD/ED is required')
        return
      }

      const res = await api.post(`/inbound/pr-draft/${prId}/add-item`, body)
      if (res.data.success) {
        Common.showPopUpSuccessMessage('Item added successfully')
        onHide()
        loadData(prId)
      }
    } catch (err) {
      const errMsg = err?.response?.data?.errors?.message || 'An error occurred'
      Common.showPopUpErrorMessage(errMsg)
    }
  }

  const disableButtonAddItemCoa = useMemo(() => {
    if (itemId) {
      const itemExist = items?.find((item) => item.id === itemId)
      if (!itemExist) return true

      const itemCoas = detail?.items_coa || []
      const itemCoaExist = itemCoas.filter(
        (itemCoa) => itemCoa.item_id === itemId
      )

      const totalQtyRemaining =
        Number(itemExist?.remaining_qty || 0) -
        Number(
          itemCoaExist?.reduce(
            (total, itemCoa) => total + (Number(itemCoa.total_qty) || 0),
            0
          ) || 0
        )

      if (Number(totalPack?.total_qty) > Number(totalQtyRemaining)) {
        return true
      }
      return false
    }
    return !itemId ? true : false
  }, [itemId, totalPack, detail?.items_coa, items])

  function handleAddBatch(item) {
    setInputBatch(item)
    setModalAddBatch(true)
  }

  const renderMenuItemChildren = (option) => (
    <div>
      {option.isNew ? (
        <h6 onClick={() => handleAddBatch(option)}>
          <Plus size={16} className="me-1" /> NEW BATCH
        </h6>
      ) : (
        <span>{option.batch}</span>
      )}
    </div>
  )

  const allOptions = [
    ...options,
    ...(newOption ? [{ ...newOption, isNew: true }] : [])
  ]

  return (
    <>
      <Modal show={show} onHide={onHide} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title
            style={{
              fontSize: '1.25rem',
              fontWeight: '700',
              color: '#333'
            }}
          >
            Add Item to Receipt
          </Modal.Title>
        </Modal.Header>
        <Formik
          initialValues={{
            item_id: '',
            batch_id: '',
            total_pack_add: ''
          }}
          onSubmit={onAddItem}
        >
          {({
            handleSubmit,
            handleChange,
            handleBlur,
            values,
            touched,
            errors,
            setFieldValue
          }) => (
            <Form onSubmit={handleSubmit}>
              <Modal.Body className="p-4">
                <Row>
                  <StyledSelectInput
                    label="item"
                    name="item_id"
                    id="item_id"
                    handleChange={(e) => {
                      const val = e.target.value
                      setFieldValue('item_id', val)
                      handleClickItem(val, setFieldValue)
                    }}
                    reff={refItems}
                    handleBlur={handleBlur}
                    touched={touched}
                    errors={errors}
                    options={items}
                    required
                    hasData
                    col={12}
                  />
                  <Form.Group className="col-12 col-md-6 mb-3">
                    <Form.Label style={labelDetailStyle}>
                      BATCH NUMBER <sup className="text-danger">*</sup>
                    </Form.Label>
                    <AsyncTypeahead
                      id="async-typeahead-example"
                      labelKey="batch"
                      onSearch={handleSearchBatch}
                      options={allOptions}
                      renderMenuItemChildren={renderMenuItemChildren}
                      onChange={(e) => {
                        const selected = e.length > 0 ? e[0] : ''
                        setInputBatch(selected)
                        setBatchId(selected ? selected.id : 0)
                        setBatchSelectedDetail(selected ? selected : null)
                        setFieldValue('batch_id', selected ? selected.id : '')
                      }}
                      inputProps={{
                        value: inputBatch?.batch || inputBatch || '',
                        className: 'border-0 bg-light px-3 form-control',
                        style: {
                          fontSize: '0.9rem',
                          height: '40px',
                          borderRadius: '8px'
                        }
                      }}
                      placeholder="Search or input batch"
                      disabled={!itemId || itemId === '0'}
                      ref={refBatch}
                    />
                  </Form.Group>
                  <StyledTextField
                    label="receiving pack"
                    name="total_pack_add"
                    type="number"
                    value={values.total_pack_add}
                    handleChange={(e) => {
                      handleChange(e)
                      setTotalPack((prevData) => {
                        const uomObj =
                          typeof dataItemSelect?.uom === 'object'
                            ? dataItemSelect?.uom
                            : null
                        return {
                          ...prevData,
                          total_qty:
                            e.target.value *
                            (selectedUom?.convertion_factor || 0),
                          conversion_uom: selectedUom?.uom_name || '',
                          conversion_qty_uom: uomObj
                            ? uomObj.conversion_uom
                            : dataItemSelect?.uom || '',
                          conversion_factor: selectedUom?.convertion_factor || 0
                        }
                      })
                    }}
                    handleBlur={handleBlur}
                    touched={touched}
                    errors={errors}
                    col={6}
                    required
                    placeholder="Input quantity"
                    disabled={!itemId || itemId === '0'}
                  />

                  <StyledSelectInput
                    label="receiving uom"
                    name="receiving_uom"
                    value={selectedUom?.uom_name || ''}
                    handleChange={(e) => {
                      const val = e.target.value
                      setFieldValue('receiving_uom', val)
                      const uom = uomOptions.find((u) => u.uom_name === val)
                      setSelectedUom(uom)
                      setTotalPack((prevData) => ({
                        ...prevData,
                        total_qty:
                          values.total_pack_add * (uom?.convertion_factor || 0),
                        conversion_uom: uom?.uom_name || '',
                        conversion_factor: uom?.convertion_factor || 0
                      }))
                    }}
                    handleBlur={handleBlur}
                    touched={touched}
                    errors={errors}
                    options={uomOptions}
                    col={6}
                    required
                    disabled={!itemId || itemId === '0'}
                  />

                  <StyledTextField
                    label="base uom qty"
                    name="total_qty"
                    value={totalPack.total_qty}
                    touched={touched}
                    errors={errors}
                    col={6}
                    required
                    disabled
                  />
                </Row>
              </Modal.Body>
              <Modal.Footer className="border-0 p-4 pt-0">
                <Button variant="light" className="px-4 py-2" onClick={onHide}>
                  Cancel
                </Button>
                <Button
                  className="btn btn-primary-blue px-4 py-2"
                  type="submit"
                  disabled={
                    !itemId ||
                    itemId === '0' ||
                    itemId === 0 ||
                    !batchId ||
                    batchId === '0' ||
                    batchId === 0 ||
                    !values.total_pack_add ||
                    disableButtonAddItemCoa
                  }
                >
                  Add Item
                </Button>
              </Modal.Footer>
            </Form>
          )}
        </Formik>
      </Modal>

      <ModalAddBatch
        show={modalAddBatch}
        onHide={() => setModalAddBatch(false)}
        item={{ id: itemId, name: dataItemSelect?.item_name }}
        batch={inputBatch}
        setBatchId={setBatchId}
        setInputBatch={setInputBatch}
      />
    </>
  )
}

export default ModalAddItemPr
